import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Droplets,
  Gem,
  Heart,
  Package,
  RefreshCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Truck,
  ChevronDown,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl } from "@/lib/supabase/client";
import { parseLines, splitProductTagsAndMeta } from "@/lib/shop-product-meta";

type ShopProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  priceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  tags: string[];
  imageUrl?: string;
  imageUrls?: string[];
};

type CatalogResponse = {
  ok: true;
  products: ShopProduct[];
};

type ProductReview = {
  name: string;
  message: string;
  rating: number;
  verifiedPurchase?: boolean;
};

type ProductReviewEligibility = {
  canReview: boolean;
  hasPaidPurchase: boolean;
  alreadyReviewed: boolean;
};

type ShopActionPayload = {
  action: string;
  productId?: string;
  userId?: string;
  userEmail?: string;
  context?: string;
  metadata?: Record<string, unknown>;
};

const PRODUCT_CART_STORAGE_KEY = "numeroloji_product_cart_v1";

function addProductToCartStorage(productId: string) {
  const normalized = String(productId ?? "").trim();
  if (!normalized) return;

  try {
    const raw = globalThis.localStorage?.getItem(PRODUCT_CART_STORAGE_KEY) ?? "[]";
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];

    const list = Array.isArray(parsed)
      ? parsed
          .map((v) => {
            if (typeof v === "string") {
              const id = String(v).trim();
              return id ? { productId: id, qty: 1 } : null;
            }
            const obj = v as any;
            const id = String(obj?.productId ?? "").trim();
            const qty = Math.max(1, Math.min(99, Number(obj?.qty ?? 1) || 1));
            return id ? { productId: id, qty } : null;
          })
          .filter(Boolean) as Array<{ productId: string; qty: number }>
      : [];

    const next = list.map((item) => ({ ...item }));
    const existing = next.find((item) => item.productId === normalized);
    if (existing) {
      existing.qty = Math.min(99, existing.qty + 1);
    } else {
      next.push({ productId: normalized, qty: 1 });
    }

    globalThis.localStorage?.setItem(PRODUCT_CART_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore localStorage errors
  }
}

async function recordShopAction(payload: ShopActionPayload) {
  try {
    await fetch("/api/numeroloji/shop-actions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    // ignore logging errors in UI flow
  }
}

async function fetchProducts(): Promise<ShopProduct[]> {
  try {
    const res = await fetch("/api/numeroloji/catalog");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) return [];
    const data = json as CatalogResponse;
    return Array.isArray(data?.products) ? data.products : [];
  } catch {
    return [];
  }
}

function readAccessTokenFromStorage() {
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
    if (!storageKey) return "";
    const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
    const parsed = raw ? (JSON.parse(raw) as any) : null;
    return typeof parsed?.access_token === "string" ? parsed.access_token : "";
  } catch {
    return "";
  }
}

async function fetchProductReviews(productId: string): Promise<ProductReview[]> {
  try {
    const res = await fetch(`/api/numeroloji/product-reviews?productId=${encodeURIComponent(productId)}`);
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok || !json?.ok) {
      throw new Error(typeof json?.error === "string" ? json.error : "Yorumlar alınamadı.");
    }

    const rows = Array.isArray(json?.reviews) ? json.reviews : [];
    return rows.map((row: any) => ({
      name: String(row?.name ?? ""),
      message: String(row?.message ?? ""),
      rating: Math.max(1, Math.min(5, Number(row?.rating ?? 5) || 5)),
      verifiedPurchase: Boolean(row?.verifiedPurchase),
    }));
  } catch {
    return [];
  }
}

async function fetchProductReviewEligibility(productId: string, token: string): Promise<ProductReviewEligibility> {
  try {
    const res = await fetch(`/api/numeroloji/product-reviews/eligibility?productId=${encodeURIComponent(productId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok || !json?.ok) {
      throw new Error(typeof json?.error === "string" ? json.error : "Yorum uygunluğu alınamadı.");
    }

    return {
      canReview: Boolean(json?.canReview),
      hasPaidPurchase: Boolean(json?.hasPaidPurchase),
      alreadyReviewed: Boolean(json?.alreadyReviewed),
    };
  } catch {
    return {
      canReview: false,
      hasPaidPurchase: false,
      alreadyReviewed: false,
    };
  }
}

async function submitProductReview(input: { token: string; productId: string; rating: number; message: string; name?: string }) {
  try {
    const res = await fetch("/api/numeroloji/product-reviews", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${input.token}`,
      },
      body: JSON.stringify({
        productId: input.productId,
        rating: input.rating,
        message: input.message,
        name: input.name,
      }),
    });

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok || !json?.ok) {
      throw new Error(typeof json?.error === "string" ? json.error : "Yorum gönderilemedi.");
    }

    return json as { ok: true };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message || "Yorum gönderilemedi.");
    }
    throw new Error("Yorum gönderilemedi.");
  }
}

function normalizeSearchText(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z-\s/]+/g, "");
}

function normalizeTag(value: string) {
  return normalizeSearchText(value).replace(/\s+/g, "");
}

function normalizeCategoryParent(value: string) {
  const key = normalizeSearchText(value).replace(/[^a-z0-9-]/g, "");
  if (["stone", "tas", "dogal-tas", "dogaltas"].includes(key)) return "stone";
  if (["oil", "yag", "ucucu-yag", "ucucuyag", "aroma-yag"].includes(key)) return "oil";
  if (["bundle", "paket", "set", "kombin"].includes(key)) return "bundle";
  if (["other", "diger"].includes(key)) return "other";
  return key || "other";
}

function categoryIcon(category: string) {
  const parent = normalizeCategoryParent(String(category ?? "").split(/[/>]/)[0] ?? "");
  switch (parent) {
    case "stone":
      return Gem;
    case "oil":
      return Droplets;
    case "bundle":
      return Package;
    default:
      return Sparkles;
  }
}

function categoryLabel(category: string) {
  const parts = String(category ?? "")
    .split(/[/>]/)
    .map((part) => part.trim())
    .filter(Boolean);

  const parent = normalizeCategoryParent(parts[0] ?? "other");
  const parentLabel =
    parent === "stone" ? "Taş" : parent === "oil" ? "Yağ" : parent === "bundle" ? "Paket" : parent === "other" ? "Diğer" : parent;

  const childLabel = parts
    .slice(1)
    .map((part) => part.replace(/[-_]+/g, " "))
    .join(" / ");

  return childLabel ? `${parentLabel} / ${childLabel}` : parentLabel;
}

function getProductImages(product: ShopProduct) {
  return Array.from(
    new Set(
      [...(product.imageUrls ?? []), product.imageUrl ?? ""]
        .map((url) => String(url ?? "").trim())
        .filter(Boolean),
    ),
  ).slice(0, 6);
}

function getEffectivePrice(product: ShopProduct) {
  return typeof product.discountedPriceTry === "number" && product.discountedPriceTry > 0
    ? product.discountedPriceTry
    : product.priceTry;
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function getKarmicDebtTag(code: number) {
  return `karmikborc${code}`;
}

export default function UserPanelMagazaProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSupabaseSession();

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<"features" | "benefits" | "care">("features");
  const [isWhyExpanded, setIsWhyExpanded] = useState(false);
  const [reviewName, setReviewName] = useState("");
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const trackedViewProductRef = useRef("");

  const profileInput = useMemo(() => {
    const firstName = typeof user?.metadata?.first_name === "string" ? (user?.metadata?.first_name as string) : "";
    const lastName = typeof user?.metadata?.last_name === "string" ? (user?.metadata?.last_name as string) : "";
    const fullName = `${firstName} ${lastName}`.trim();

    const birthDay = typeof user?.metadata?.birth_day === "number" ? user?.metadata?.birth_day : Number(user?.metadata?.birth_day);
    const birthMonth =
      typeof user?.metadata?.birth_month === "number" ? user?.metadata?.birth_month : Number(user?.metadata?.birth_month);
    const birthYear = typeof user?.metadata?.birth_year === "number" ? user?.metadata?.birth_year : Number(user?.metadata?.birth_year);

    if (!fullName || !Number.isFinite(birthDay) || !Number.isFinite(birthMonth) || !Number.isFinite(birthYear)) return null;

    return {
      fullName,
      birthDate: parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear }),
    };
  }, [user?.metadata?.birth_day, user?.metadata?.birth_month, user?.metadata?.birth_year, user?.metadata?.first_name, user?.metadata?.last_name]);

  const karmicCodes = useMemo(() => {
    if (!profileInput) return [] as number[];
    return computeKarmaNumerology(profileInput.fullName, profileInput.birthDate).karmicCodes ?? [];
  }, [profileInput]);

  const productsQuery = useQuery({
    queryKey: ["panel-shop-products"],
    queryFn: fetchProducts,
    staleTime: 30_000,
    retry: false,
  });

  const allProducts = useMemo(() => (productsQuery.data ?? []).filter((p) => p && p.active), [productsQuery.data]);

  const product = useMemo(() => {
    const productId = String(id ?? "").trim();
    if (!productId) return null;
    return allProducts.find((p) => p.id === productId) ?? null;
  }, [allProducts, id]);

  const accessToken = useMemo(() => readAccessTokenFromStorage(), [user?.id]);

  const productImages = useMemo(() => (product ? getProductImages(product) : []), [product]);

  const { cleanTags: productTagsClean, meta } = useMemo(() => splitProductTagsAndMeta(product?.tags ?? []), [product?.tags]);

  const galleryImages = useMemo(() => {
    const fromMeta = parseLines(meta.galleryUrlsText);
    return Array.from(new Set([...productImages, ...fromMeta].map((url) => String(url ?? "").trim()).filter(Boolean))).slice(0, 8);
  }, [meta.galleryUrlsText, productImages]);

  useEffect(() => {
    if (!galleryImages.length) {
      setActiveImageIndex(0);
      return;
    }

    setActiveImageIndex((current) => (current >= galleryImages.length ? 0 : current));
  }, [galleryImages]);

  const recommendedProducts = useMemo(() => {
    if (!product) return [] as ShopProduct[];

    const productTags = new Set(productTagsClean.map(normalizeTag));
    const targetCategoryParent = normalizeCategoryParent(String(product.category ?? "").split(/[/>]/)[0] ?? "");
    const karmicWanted = new Set(karmicCodes.map((code) => getKarmicDebtTag(code)).map(normalizeTag));

    const score = (candidate: ShopProduct) => {
      let s = 0;
      const { cleanTags } = splitProductTagsAndMeta(candidate.tags ?? []);
      const candidateTags = new Set(cleanTags.map(normalizeTag));
      const candidateParent = normalizeCategoryParent(String(candidate.category ?? "").split(/[/>]/)[0] ?? "");

      if (candidateParent === targetCategoryParent) s += 4;
      for (const tag of candidateTags) {
        if (productTags.has(tag)) s += 3;
      }
      for (const wanted of karmicWanted) {
        if (candidateTags.has(wanted)) s += 5;
      }
      return s;
    };

    return allProducts
      .filter((p) => p.id !== product.id)
      .map((p) => ({ p, score: score(p) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((item) => item.p);
  }, [allProducts, karmicCodes, product, productTagsClean]);

  const featureItems = useMemo(
    () =>
      parseLines(meta.featuresText).length
        ? parseLines(meta.featuresText)
        : [
            "%100 Gerçek ve A+ kalite doğal taş",
            "Özel enerji dengeleme niyetiyle hazırlanır",
            "Esnek kullanım yapısı ile günlük kullanıma uygundur",
            "Kişisel enerji alanına uyum için öneri notu içerir",
          ],
    [meta.featuresText],
  );

  const spiritualBenefits = useMemo(
    () =>
      meta.spiritualBenefitsText ||
      "Bu ürün, kişinin enerji alanını dengelemeye yardımcı olur. Özellikle zihinsel sakinlik, odak ve iç denge hedeflerinde destekleyici olarak kullanılır.",
    [meta.spiritualBenefitsText],
  );

  const careText = useMemo(
    () =>
      meta.careText ||
      "Taşınızı haftada bir kısa süre akan su altında temizleyebilir, ardından kuru bir bezle nazikçe kurulayabilirsiniz. Parfüm ve kimyasallardan uzak tutmanız önerilir.",
    [meta.careText],
  );

  const stoneTags = useMemo(() => {
    const fromMeta = String(meta.stonePowerTagsText ?? "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    return fromMeta.length ? fromMeta : ["Koruma", "Denge", "İletişim"];
  }, [meta.stonePowerTagsText]);

  const productReviewsQuery = useQuery({
    queryKey: ["product-reviews", product?.id],
    queryFn: () => fetchProductReviews(String(product?.id ?? "")),
    enabled: Boolean(product?.id),
    staleTime: 15_000,
    retry: false,
  });

  const reviewEligibilityQuery = useQuery({
    queryKey: ["product-review-eligibility", product?.id, Boolean(accessToken)],
    queryFn: () => fetchProductReviewEligibility(String(product?.id ?? ""), accessToken),
    enabled: Boolean(product?.id && accessToken),
    staleTime: 15_000,
    retry: false,
  });

  const createReviewMutation = useMutation({
    mutationFn: (payload: { token: string; productId: string; rating: number; message: string; name?: string }) => submitProductReview(payload),
    onSuccess: async () => {
      setReviewMessage("");
      setReviewRating(5);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product-reviews", product?.id] }),
        queryClient.invalidateQueries({ queryKey: ["product-review-eligibility", product?.id] }),
      ]);
      logAction("review_submit", { rating: reviewRating });
    },
  });

  const reviews = useMemo(() => {
    const fromDb = productReviewsQuery.data ?? [];
    return fromDb.slice(0, 12);
  }, [productReviewsQuery.data]);

  const ratingAvg = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length;
  }, [reviews]);

  const discountPercent = useMemo(() => {
    if (!(typeof product?.discountedPriceTry === "number" && product.discountedPriceTry > 0 && product.priceTry > product.discountedPriceTry)) {
      return 0;
    }
    return Math.max(1, Math.round(((product.priceTry - product.discountedPriceTry) / product.priceTry) * 100));
  }, [product]);

  const logAction = (action: string, metadata?: Record<string, unknown>) => {
    void recordShopAction({
      action,
      productId: product?.id,
      userId: user?.id,
      userEmail: typeof user?.email === "string" ? user.email : undefined,
      context: "panel-magaza-product",
      metadata,
    });
  };

  const goToCheckout = (productId: string, source: "buy_now" | "add_to_cart") => {
    logAction(source === "buy_now" ? "buy_now_click" : "add_to_cart_click", { source });

    const params = new URLSearchParams();
    params.set("productIds", productId);
    params.set("from", "panel-magaza");

    if (source === "add_to_cart") {
      addProductToCartStorage(productId);
      navigate(`/numeroloji/sepet?${params.toString()}`);
      return;
    }

    navigate(`/numeroloji/satin-al?${params.toString()}`);
  };

  useEffect(() => {
    const pid = String(product?.id ?? "").trim();
    if (!pid) return;
    if (trackedViewProductRef.current === pid) return;
    trackedViewProductRef.current = pid;
    logAction("product_view");
  }, [product?.id]);

  if (productsQuery.isLoading) {
    return <div className="mx-auto w-full max-w-6xl text-sm text-slate-500 dark:text-slate-400">Ürün yükleniyor...</div>;
  }

  if (productsQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl text-sm text-red-600">
        {productsQuery.error instanceof Error ? productsQuery.error.message : "Ürün alınamadı."}
      </div>
    );
  }

  if (!product) {
    return (
      <Card className="mx-auto w-full max-w-4xl border-slate-200 bg-white p-6 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
        <div className="text-lg font-bold text-slate-900 dark:text-slate-100">Ürün bulunamadı</div>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Bu ürün kaldırılmış olabilir veya bağlantı geçersiz.</p>
        <Button className="mt-4 rounded-lg" onClick={() => navigate("/panel/magaza")}>Mağazaya Dön</Button>
      </Card>
    );
  }

  const Icon = categoryIcon(product.category);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-10 pb-10">
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Link to="/panel/magaza" className="hover:text-[var(--panel-primary)]">Ana Sayfa</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span>{categoryLabel(product.category)}</span>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="font-semibold text-slate-700 dark:text-slate-200">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <section className="space-y-4">
          <div className="aspect-square overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            {galleryImages[activeImageIndex] ? (
              <img src={galleryImages[activeImageIndex]} alt={product.name} className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full place-items-center text-sm text-slate-500">Ürün görseli yakında eklenecek</div>
            )}
          </div>

          {galleryImages.length ? (
            <div className="grid grid-cols-4 gap-4">
              {galleryImages.map((url, idx) => (
                <button
                  key={`${url}-${idx}`}
                  type="button"
                  className={`aspect-square overflow-hidden rounded-lg border transition-opacity ${idx === activeImageIndex ? "border-2 border-[var(--panel-primary)] opacity-100" : "border-slate-200 opacity-70 hover:opacity-100 dark:border-slate-800"}`}
                  onClick={() => {
                    setActiveImageIndex(idx);
                    logAction("gallery_image_select", { imageIndex: idx + 1 });
                  }}
                >
                  <img src={url} alt={`${product.name} ${idx + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          ) : null}
        </section>

        <section className="flex flex-col gap-6">
          <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[var(--panel-primary)]/10 px-3 py-1 text-sm font-semibold text-[var(--panel-primary)]">
            <Sparkles className="h-4 w-4" />
            <span>{meta.energyBadgeText || (karmicCodes.length ? `Senin ${karmicCodes.join(", ")} Enerjin İçin Öneriliyor` : "Sana Özel Öneri")}</span>
          </div>

          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white">{product.name}</h1>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Star key={`star-${idx}`} className={`h-4 w-4 ${idx < Math.round(ratingAvg) ? "fill-current" : ""}`} />
                ))}
              </div>
              <span className="text-slate-500 dark:text-slate-400">{reviews.length} Değerlendirme</span>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <span className="text-3xl font-bold text-[var(--panel-primary)]">{formatTry(getEffectivePrice(product))}</span>
            {typeof product.discountedPriceTry === "number" && product.discountedPriceTry > 0 && product.discountedPriceTry < product.priceTry ? (
              <>
                <span className="text-xl text-slate-400 line-through">{formatTry(product.priceTry)}</span>
                <span className="rounded bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-700">%{discountPercent} İndirim</span>
              </>
            ) : null}
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400">
            {meta.installmentText || "Kredi kartıyla güvenli ödeme imkanı."}
          </p>

          <div className="rounded-xl border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/5 p-4">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
              <Icon className="h-4 w-4 text-[var(--panel-primary)]" />
              {meta.whyThisProductTitle || "Neden Bu Ürün?"}
            </h3>

            <div className="relative mt-2">
              <p
                className={`text-sm leading-relaxed text-slate-600 transition-all duration-300 dark:text-slate-300 ${
                  isWhyExpanded ? "max-h-[999px]" : "max-h-[4.4rem] overflow-hidden"
                }`}
              >
                {meta.whyThisProductText || product.description}
              </p>

              {!isWhyExpanded ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[var(--panel-primary)]/10 to-transparent" />
              ) : null}
            </div>

            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--panel-primary)]"
              onClick={() => {
                const nextExpanded = !isWhyExpanded;
                setIsWhyExpanded(nextExpanded);
                logAction("toggle_why_this_product", { expanded: nextExpanded });
              }}
            >
              <span>{isWhyExpanded ? "Daha az göster" : "Devamını oku"}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isWhyExpanded ? "rotate-180" : ""}`} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex gap-4">
              <Button className="h-14 flex-1 rounded-xl text-sm font-bold" onClick={() => goToCheckout(product.id, "buy_now")}>
                HEMEN AL
              </Button>
              <Button variant="outline" className="h-14 flex-1 rounded-xl border-2 border-[var(--panel-primary)] text-sm font-bold text-[var(--panel-primary)]" onClick={() => goToCheckout(product.id, "add_to_cart")}>
                SEPETE EKLE
              </Button>
            </div>

            <button
              type="button"
              className="mx-auto flex items-center gap-2 text-sm text-slate-500 hover:text-[var(--panel-primary)]"
              onClick={() => logAction("favorite_click")}
            >
              <Heart className="h-4 w-4" />
              Favorilerime Ekle
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 border-t border-slate-200 pt-5 dark:border-slate-800">
            <div className="flex flex-col items-center gap-1 text-center">
              <Truck className="h-5 w-5 text-[var(--panel-primary)]" />
              <span className="text-[11px] font-semibold">Ücretsiz Kargo</span>
            </div>
            <div className="flex flex-col items-center gap-1 text-center">
              <ShieldCheck className="h-5 w-5 text-[var(--panel-primary)]" />
              <span className="text-[11px] font-semibold">Güvenli Ödeme</span>
            </div>
            <div className="flex flex-col items-center gap-1 text-center">
              <RefreshCcw className="h-5 w-5 text-[var(--panel-primary)]" />
              <span className="text-[11px] font-semibold">Kolay İade</span>
            </div>
          </div>
        </section>
      </div>

      <section className="space-y-8">
        <div className="flex gap-6 border-b border-slate-200 dark:border-slate-800">
          <button
            type="button"
            className={`pb-3 text-sm font-semibold ${activeTab === "features" ? "border-b-2 border-[var(--panel-primary)] text-[var(--panel-primary)]" : "text-slate-500"}`}
            onClick={() => {
              setActiveTab("features");
              logAction("details_tab_change", { tab: "features" });
            }}
          >
            Ürün Özellikleri
          </button>
          <button
            type="button"
            className={`pb-3 text-sm font-semibold ${activeTab === "benefits" ? "border-b-2 border-[var(--panel-primary)] text-[var(--panel-primary)]" : "text-slate-500"}`}
            onClick={() => {
              setActiveTab("benefits");
              logAction("details_tab_change", { tab: "benefits" });
            }}
          >
            Spiritüel Faydalar
          </button>
          <button
            type="button"
            className={`pb-3 text-sm font-semibold ${activeTab === "care" ? "border-b-2 border-[var(--panel-primary)] text-[var(--panel-primary)]" : "text-slate-500"}`}
            onClick={() => {
              setActiveTab("care");
              logAction("details_tab_change", { tab: "care" });
            }}
          >
            Bakım ve Temizlik
          </button>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          <div>
            {activeTab === "features" ? (
              <ul className="space-y-3">
                {featureItems.map((item, idx) => (
                  <li key={`feature-${idx}`} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <Sparkles className="mt-0.5 h-4 w-4 text-[var(--panel-primary)]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {activeTab === "benefits" ? <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{spiritualBenefits}</p> : null}
            {activeTab === "care" ? <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{careText}</p> : null}
          </div>

          <Card className="border-slate-200 bg-white p-6 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{meta.stonePowerTitle || "Taşın Gücü"}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              {meta.stonePowerText || "Bu taşın titreşimi, kişinin enerji alanında denge ve sakinlik yaratmaya destek olur."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {stoneTags.map((tag, idx) => (
                <span key={`stone-tag-${idx}`} className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {tag}
                </span>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <section>
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{meta.reviewsTitle || "Aydınlanmış Ruhlar Yorumları"}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">{meta.reviewsSubtitle || "Kullanıcılarımızın deneyimleri"}</p>
          </div>
          <button type="button" className="text-sm font-bold text-[var(--panel-primary)] hover:underline">
            Tümünü Gör
          </button>
        </div>

        {reviews.length ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {reviews.map((review, idx) => (
              <Card key={`${review.name}-${idx}`} className="border-slate-100 bg-white p-6 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
                <div className="mb-4 flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-200 text-xs font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {review.name
                      .split(" ")
                      .map((part) => part[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{review.name}</div>
                    <div className="mt-0.5 flex items-center gap-1 text-amber-500">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={`review-star-${idx}-${i}`} className={`h-3.5 w-3.5 ${i < review.rating ? "fill-current" : ""}`} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm italic leading-relaxed text-slate-600 dark:text-slate-300">“{review.message}”</p>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 dark:text-slate-400">
            Bu ürün için henüz doğrulanmış müşteri yorumu bulunmuyor.
          </Card>
        )}

        <Card className="mt-6 border-slate-200 bg-white p-6 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
          <div className="text-base font-bold text-slate-900 dark:text-slate-100">Bu Ürünü Değerlendir</div>
          {!accessToken ? (
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Yorum yapabilmek için giriş yapmalısınız.</p>
          ) : reviewEligibilityQuery.isLoading ? (
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Uygunluk kontrol ediliyor...</p>
          ) : reviewEligibilityQuery.isError ? (
            <p className="mt-2 text-sm text-rose-600">{reviewEligibilityQuery.error instanceof Error ? reviewEligibilityQuery.error.message : "Kontrol yapılamadı."}</p>
          ) : !reviewEligibilityQuery.data?.hasPaidPurchase ? (
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Yorum yapabilmek için bu ürünü satın almış olmalısınız.</p>
          ) : reviewEligibilityQuery.data?.alreadyReviewed ? (
            <p className="mt-2 text-sm text-emerald-600">Bu ürün için yorumunuz zaten alınmış. Teşekkür ederiz.</p>
          ) : (
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!product?.id || !accessToken) return;
                createReviewMutation.mutate({
                  token: accessToken,
                  productId: product.id,
                  rating: reviewRating,
                  message: reviewMessage,
                  name: reviewName,
                });
              }}
            >
              <input
                value={reviewName}
                onChange={(e) => setReviewName(e.target.value)}
                placeholder="Görünmesini istediğiniz ad (opsiyonel)"
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
                maxLength={80}
              />
              <select
                value={String(reviewRating)}
                onChange={(e) => setReviewRating(Math.max(1, Math.min(5, Number(e.target.value) || 5)))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="5">5 - Mükemmel</option>
                <option value="4">4 - İyi</option>
                <option value="3">3 - Orta</option>
                <option value="2">2 - Geliştirilebilir</option>
                <option value="1">1 - Zayıf</option>
              </select>
              <textarea
                value={reviewMessage}
                onChange={(e) => setReviewMessage(e.target.value)}
                placeholder="Ürünü kullandıktan sonraki deneyiminizi yazın"
                className="min-h-[130px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
                maxLength={1400}
                required
              />

              {createReviewMutation.isError ? (
                <p className="text-sm text-rose-600">{createReviewMutation.error instanceof Error ? createReviewMutation.error.message : "Yorum gönderilemedi."}</p>
              ) : null}

              {createReviewMutation.isSuccess ? <p className="text-sm text-emerald-600">Yorumunuz başarıyla kaydedildi.</p> : null}

              <Button type="submit" className="rounded-lg" disabled={createReviewMutation.isPending || !reviewMessage.trim()}>
                {createReviewMutation.isPending ? "Gönderiliyor..." : "Yorumu Gönder"}
              </Button>
            </form>
          )}
        </Card>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{meta.recommendedTitle || "Bunlar da İlginizi Çekebilir"}</h2>
          <Link to="/panel/magaza" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[var(--panel-primary)]">
            Tümünü Gör
            <Search className="h-3.5 w-3.5" />
          </Link>
        </div>

        {recommendedProducts.length ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {recommendedProducts.map((item) => (
              <article
                key={item.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-[var(--panel-primary)]/10 dark:bg-slate-950"
              >
                <button type="button" className="w-full text-left" onClick={() => navigate(`/panel/magaza/urun/${encodeURIComponent(item.id)}`)}>
                  <div className="aspect-square overflow-hidden bg-slate-100 dark:bg-slate-800">
                    {getProductImages(item)[0] ? <img src={getProductImages(item)[0]} alt={item.name} className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="p-3">
                    <div className="line-clamp-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{item.name}</div>
                    <div className="mt-2 text-sm font-black text-slate-900 dark:text-white">{formatTry(getEffectivePrice(item))}</div>
                  </div>
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 dark:text-slate-400">Şu an ek öneri bulunamadı.</div>
        )}
      </section>

      <Card className="border-slate-200 bg-white p-5 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900 dark:text-slate-300">
        {karmicCodes.length
          ? `Karmik borç kodlarınıza (${karmicCodes.join(", ")}) uygun etiketli ürünler önerilerde öncelikli gösterilir.`
          : "Karmik borç kodu bulunmadığında kategori ve etiket benzerliğine göre öneri yapılır."}
      </Card>

      {!!productTagsClean.length ? (
        <div className="flex flex-wrap gap-2">
          {productTagsClean.slice(0, 8).map((tag, idx) => (
            <span
              key={`${product.id}-tag-${idx}`}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
